import React from 'react';
import {
  ContentType,
  formatMinutes,
  InformationCard,
  InformationCardState,
  useDeviceSize,
  Video,
} from '@atlassian/learning-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useLoaderData } from 'react-router-dom';

import { getDuration, shortenDurationString } from 'src/pages/LearningPathPage/utils';
import { useSaveContent } from 'src/hooks/useSaveContent';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { getOptions } from 'src/pages/LessonPage/children/utils/getOptions';
import { LearningPathLoaderProps } from 'src/pages/LearningPathPage/loader';
import { useCourseProgress, useLessonProgress } from 'src/store/progress/progressStore';
import { LearningProgressStatus } from 'src/services/http/progress';
import { getSlug } from 'src/utils/slug';
import { getVideoAttrs } from 'src/utils/contentful';
import { OrVideo } from 'src/graphql/generated/graphql';
import { DESKTOP_CONTROLS, MOBILE_CONTROLS, FIELD_ID } from 'src/pages/LearningPathPage/constants';
import { useLearningPathNavigation } from 'src/pages/LearningPathPage/utils/learningPathNavigation';
import { useStartContent } from 'src/hooks/useStartContent';

interface InformationCardWrapperProps {
  urlToShare: string;
}

export const LearningPathInformationCard: React.FC<InformationCardWrapperProps> = ({ urlToShare }) => {
  const { learningPath } = useLoaderData() as LearningPathLoaderProps;
  const { user, isLoading } = useAtlassianUser();
  const { isContentSaved, saveContent } = useSaveContent({
    contentId: learningPath?.sys.id,
    contentType: ContentType.LEARNING_PATH,
    learningPathId: learningPath?.sys.id,
    user,
  });
  const deviceWidth = useDeviceSize();
  const [{ progressForCourses, isLoadingProgressForCourses }] = useCourseProgress();
  const [{ progressForLessons }] = useLessonProgress();
  const videoAttrs = getVideoAttrs(learningPath?.video as unknown as OrVideo);
  const customControls = {
    customMobileControls: MOBILE_CONTROLS,
    customDesktopControls: DESKTOP_CONTROLS,
  };
  const video = videoAttrs && (
    <Video
      name={learningPath.video?.name}
      videoId={learningPath.video?.sys?.id}
      captions={videoAttrs.cdnCaptions || ''}
      poster={videoAttrs.thumbnailUrl}
      controls={true}
      width={learningPath?.video?.videoWidth || '100%'}
      height={learningPath?.video?.videoHeight || 'auto'}
      orangeUrl={videoAttrs.cdnUrl}
      vimeoUrl={videoAttrs.vimeoUrl}
      youtubeUrl={videoAttrs.youtubeUrl}
      hasResponsiveControls={true}
      customControls={customControls}
    />
  );
  const learningPathSlug = getSlug(learningPath);
  const learningObjectives = learningPath?.learningObjectives?.json
    ? documentToReactComponents(
      learningPath.learningObjectives.json,
      getOptions(learningPath.learningObjectives, deviceWidth),
    )
    : '';

  const learningPathDuration = shortenDurationString(
    formatMinutes(learningPath?.coursesCollection.items.reduce((acc, course) => acc + getDuration(course), 0)),
  );

  const calculateProgress = (): number => {
    if (!progressForCourses || !learningPath) {
      return 0;
    }

    if (progressForCourses.overallStatus === LearningProgressStatus.COMPLETED) {
      return 100;
    }

    const learningPathLessonIds = new Set(
      learningPath.coursesCollection.items.flatMap((course) =>
        course.lessonsCollection.items.map((lesson) => lesson.sys.id),
      ),
    );

    const numberOfCompletedEntities =
      progressForLessons?.lessons.filter(
        (progressForLesson) =>
          learningPathLessonIds.has(progressForLesson.lessonId) &&
          progressForLesson.status === LearningProgressStatus.COMPLETED,
      ).length || 0;

    const overallEntities = learningPath.coursesCollection.items.reduce(
      (acc, course) => acc + course.lessonsCollection.items.length,
      0,
    );

    return Math.round((numberOfCompletedEntities / overallEntities) * 100);
  };

  const { navigateToCourse, navigateToLesson } = useLearningPathNavigation();

  const startLearningPath = useStartContent({
    items: learningPath?.coursesCollection?.items,
    progress: progressForCourses,
    contentSlug: learningPathSlug,
    contentType: ContentType.LEARNING_PATH,
    userId: user?.account_id,
    navigateToItem: navigateToCourse,
    fieldId: FIELD_ID,
    navigateToLesson,
    lessonsProgress: progressForLessons,
  });

  const getCourseState = (): InformationCardState => {
    if (isLoading || isLoadingProgressForCourses) {
      return InformationCardState.LOADING;
    }

    if (!user || !progressForCourses) {
      return InformationCardState.DEFAULT;
    }

    if (progressForCourses.overallStatus === LearningProgressStatus.IN_PROGRESS) {
      return InformationCardState.IN_PROGRESS;
    }

    if (progressForCourses.overallStatus === LearningProgressStatus.COMPLETED) {
      return InformationCardState.FINISHED;
    }

    return InformationCardState.DEFAULT;
  };

  return (
    <InformationCard
      video={video}
      data-testid="information-card"
      contentType={ContentType.LEARNING_PATH}
      progress={calculateProgress()}
      duration={learningPathDuration}
      description={learningObjectives}
      cardState={getCourseState()}
      isSaved={isContentSaved}
      onSave={user && saveContent}
      urlToShare={urlToShare}
      onClick={startLearningPath}
      title={learningPath?.learningObjectivesTitle}
    />
  );
};
